import {
  PERSONA_CHAT_IMAGE_JPEG_QUALITY,
  PERSONA_CHAT_IMAGE_MAX_DIMENSION_PX,
  PERSONA_CHAT_IMAGE_OUTPUT_MIME_TYPE,
} from '@highlight-cp/constants';

import { resolveFileMimeType } from './resolve-file-mime-type';

export const computePersonaChatImageDimensions = (
  width: number,
  height: number,
  maxDimension = PERSONA_CHAT_IMAGE_MAX_DIMENSION_PX,
): { width: number; height: number } => {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }

  const factor = width > height ? maxDimension / width : maxDimension / height;

  return {
    width: Math.round(width * factor),
    height: Math.round(height * factor),
  };
};

export const shouldSkipPersonaChatImageProcessing = (
  file: File,
  width: number,
  height: number,
  maxDimension = PERSONA_CHAT_IMAGE_MAX_DIMENSION_PX,
): boolean => {
  const isJpeg = resolveFileMimeType(file) === PERSONA_CHAT_IMAGE_OUTPUT_MIME_TYPE;

  return isJpeg && width <= maxDimension && height <= maxDimension;
};

export const toPersonaChatImageFileName = (originalName: string): string => {
  const baseName = originalName.replace(/\.[^.]+$/, '') || 'image';
  return `${baseName}.jpg`;
};

export interface ResizePersonaChatImageFileOptions {
  maxDimension?: number;
  quality?: number;
}

/**
 * Resizes and JPEG-encodes persona chat images before Bedrock vision calls.
 * Uses `createImageBitmap` + canvas (no extra dependencies).
 */
export const resizePersonaChatImageFile = async (
  file: File,
  options: ResizePersonaChatImageFileOptions = {},
): Promise<File> => {
  const maxDimension = options.maxDimension ?? PERSONA_CHAT_IMAGE_MAX_DIMENSION_PX;
  const quality = options.quality ?? PERSONA_CHAT_IMAGE_JPEG_QUALITY;

  const bitmap = await createImageBitmap(file);

  try {
    if (shouldSkipPersonaChatImageProcessing(file, bitmap.width, bitmap.height, maxDimension)) {
      return file;
    }

    const { width, height } = computePersonaChatImageDimensions(
      bitmap.width,
      bitmap.height,
      maxDimension,
    );

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context is not available');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, PERSONA_CHAT_IMAGE_OUTPUT_MIME_TYPE, quality);
    });

    if (!blob) {
      throw new Error('Failed to encode image as JPEG');
    }

    return new File([blob], toPersonaChatImageFileName(file.name), {
      type: PERSONA_CHAT_IMAGE_OUTPUT_MIME_TYPE,
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
  }
};
